import cartRequest from './cartRequest';
import getClientId from '../storage/getClientId';
import getVisitorId from '../storage/getVisitorId';
import trackCartError from './trackCartError';

/**
 * Counts the number of items in a checkout cart
 *
 * @param {Object} cartData Checkout cart data
 * @returns {Integer} Number of items in the cart
 */
export const cartTotal = (cartData) => {
  const lineItems = cartData?.relationships?.line_items?.data;
  return lineItems?.length ? lineItems.length : 0;
};

/**
 * Fetches the visitor or client checkout cart for the current user
 *
 * @returns {Object} Checkout cart data
 */
const fetchCart = async () => {
  const url = `https://${process.env.API_HOST}/v1/carts`;
  const ownerData = _getOwnerData();

  if (!ownerData) {
    const visitorIdTag = document.head.querySelector('meta[name=visitor-id]');
    const visitorId = visitorIdTag?.content;
    const clientIdTag = document.head.querySelector('meta[name=client-id]');
    const clientId = clientIdTag?.content;
    const cookies = document.cookie || 'none';
    const errorMsg = 'No owner data for POST to /v1/carts';
    const details = `data: { ownerData: ${ownerData}, `
                    + `url: ${url}, `
                    + `visitorId: ${visitorId}, `
                    + `clientId: ${clientId}, `
                    + `cookies: ${cookies}, `
                    + `windowVT: ${window.vt} }`;
    trackCartError(new Error(errorMsg), details);
    return;
  } else {
    const data = {
      attributes: {
        cart_type: 'checkout',
        owner_id: ownerData.id,
        owner_type: ownerData.type
      },
      type: 'carts'
    };

    return await cartRequest(url, data)
      .then((json) => {
        const cartData = json?.data;

        if (!cartData) {
          const errorMsg = 'No cart data returned';
          trackCartError(new Error(errorMsg), errorMsg);
          return;
        }

        if (cartData.attributes?.cart_type !== 'checkout') {
          const errorMsg = 'Cart is not a checkout cart';
          trackCartError(new Error(errorMsg), errorMsg, { cartId: cartData.id });
          return;
        }

        _saveCartId(cartData);

        return cartData;
      })
      .catch((error) => {
        trackCartError(error, 'Cart fetch failure');
      });
    }
};

/**
 * Returns owner data required to fetch the cart. If user is signed in and a client,
 * will return data for a client cart; otherwise will return data for a visitor cart
 *
 * @returns {type} Type of user — either 'client' or 'visitor'
 * @returns {id} ID of user — either client ID or visitor ID
 */
const _getOwnerData = () => {
  const clientID = getClientId();
  const visitorID = getVisitorId();

  if (clientID && clientID.length) {
    return { type: 'client', id: clientID };
  } else if (visitorID && visitorID.length) {
    return { type: 'visitor', id: visitorID };
  }
};

/**
 * Saves the checkout cart id as a window variable for other cart requests to access
 *
 * @param {Object} cartData Checkout cart data
 */
const _saveCartId = (cartData) => {
  if (!cartData?.id) {
    const errorMsg = 'Cart ID missing';
    trackCartError(new Error(errorMsg), errorMsg);
    return;
  }

  window.vt = window.vt || {};
  window.vt.cart = window.vt.cart || {};
  window.vt.cart.id = cartData.id;
};

export default fetchCart;
